import { CANDIDATE_STORAGE_KEY, REACT_APP_SERVER_BASE_URL, REACT_APP_SERVER_BASE_URL_QT } from "../constants/config";

export const getServerBaseUrl = () => {
  if (!isEmpty(REACT_APP_SERVER_BASE_URL)) {
    return REACT_APP_SERVER_BASE_URL;
  }

  return null;
};

export const getServerBaseUrlQT = () => {
  if (!isEmpty(REACT_APP_SERVER_BASE_URL_QT)) {
    return REACT_APP_SERVER_BASE_URL_QT;
  }
  return null;
};

const has = Object.prototype.hasOwnProperty;

export const isEmpty = (prop) => {
  return (
    prop === null ||
    prop === undefined ||
    (has.call(prop, "length") && prop.length === 0) ||
    (prop.constructor === Object && Object.keys(prop).length === 0) ||
    `${prop}`.toLocaleLowerCase() === "null"
  );
};

export const findPageByPath = (currentPath, pages = []) => {
  const catchAllPage = pages.find((page) => page.path === "/*");
  if (catchAllPage) {
    return catchAllPage;
  }

  const page = pages.find((page) => {
    const path = new RegExp(
      "^" + page.path.replace(/:[^/]+/g, "([^/]+)") + "$",
    );
    return path.test(currentPath);
  });

  return page;
};

export const getFullUrlStaticFilePDF = (path) => {
  const server_url = getServerBaseUrl();
  let url = `${path}`
    .replace("_internal\\", "")
    .replace("_internal/", "")
    .replace("server\\", "")
    .replace("server/", "")
    .replace("src\\", "")
    .replace("src/", "");

  if (server_url) {
    url = `${server_url}/${url}`;
  } else {
    url = `${window.location.origin}/${url}`;
  }

  return url;
};

export const getFullUrlStaticFilePDFQT = (path) => {
  const server_url = getServerBaseUrlQT();
  let url = `${path}`
    .replace("_internal\\", "")
    .replace("_internal/", "")
    .replace("server\\", "")
    .replace("server/", "")
    .replace("src\\", "")
    .replace("src/", "");

  if (server_url) {
    url = `${server_url}/${url}`;
  } else {
    url = `${window.location.origin}/${url}`;
  }

  return url;
};

export const getUrlToOff = (path) => {
  let url = getFullUrlStaticFilePDF(path);

  if (isEmpty(REACT_APP_SERVER_BASE_URL)) {
    url = `${window.location.origin}/${url}`;
  }

  return url;
};

//  2 * 1000; 2s
const CANDIDATE_TTL_MS = 2 * 24 * 60 * 60 * 1000; // 2 days

const DEFAULT_CANDIDATE = {
  fullName: "",
  email: "",
  phone: "",
  consent: false,
};

const isCandidateExpired = (savedAt) =>
  !savedAt || typeof savedAt !== "number" || Date.now() - savedAt > CANDIDATE_TTL_MS;

export const clearCandidateFromStorage = () => {
  localStorage.removeItem(CANDIDATE_STORAGE_KEY);
  clearRecruitmentSessionFlags();
};

export const loadCandidateFromStorage = () => {
  try {
    const raw = localStorage.getItem(CANDIDATE_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_CANDIDATE };
    const parsed = JSON.parse(raw);
    if (isCandidateExpired(parsed.savedAt)) {
      clearCandidateFromStorage();
      return { ...DEFAULT_CANDIDATE };
    }
    const { savedAt, ...info } = parsed;
    return { ...DEFAULT_CANDIDATE, ...info };
  } catch {
    return { ...DEFAULT_CANDIDATE };
  }
};

export const saveCandidateToStorage = (info, { resetTimestamp = false } = {}) => {
  let savedAt = Date.now();
  if (!resetTimestamp) {
    try {
      const raw = localStorage.getItem(CANDIDATE_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (!isCandidateExpired(parsed.savedAt)) {
          savedAt = parsed.savedAt;
        }
      }
    } catch {
      // keep new savedAt
    }
  }
  localStorage.setItem(
    CANDIDATE_STORAGE_KEY,
    JSON.stringify({ ...info, savedAt }),
  );
};

export const hasCandidateInfo = (info) =>
  Boolean(
    info?.fullName?.trim() &&
    info?.email?.trim() &&
    info?.phone?.trim() &&
    info?.consent,
  );

const RECRUITMENT_SESSION_ACTIVE_KEY = "recruitment_session_active";

const recruitmentSessionFlagKey = (campaignId) =>
  `${RECRUITMENT_SESSION_ACTIVE_KEY}_${String(campaignId)}`;

export const markRecruitmentSessionActive = (campaignId) => {
  if (!campaignId) return;
  sessionStorage.setItem(recruitmentSessionFlagKey(campaignId), "1");
};

export const hasRecruitmentSessionActive = (campaignId) =>
  Boolean(
    campaignId &&
    sessionStorage.getItem(recruitmentSessionFlagKey(campaignId)) === "1",
  );

export const clearRecruitmentSessionFlags = () => {
  const keysToRemove = [];
  for (let index = 0; index < sessionStorage.length; index += 1) {
    const key = sessionStorage.key(index);
    if (key?.startsWith(`${RECRUITMENT_SESSION_ACTIVE_KEY}_`)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => sessionStorage.removeItem(key));
};

/** @deprecated Use hasRecruitmentSessionActive — token is now in HttpOnly cookie */
export const loadChatSessionsFromStorage = () => ({});

/** @deprecated Use markRecruitmentSessionActive */
export const saveChatSessionToStorage = (campaignId) => {
  markRecruitmentSessionActive(campaignId);
};

/** @deprecated Use hasRecruitmentSessionActive */
export const getChatSessionToken = (campaignId) =>
  hasRecruitmentSessionActive(campaignId) ? "active" : null;

const OPEN_CHAT_STATE_KEY = "recruitment_open_chat_state";

export const loadOpenChatStateFromStorage = () => {
  try {
    const raw = sessionStorage.getItem(OPEN_CHAT_STATE_KEY);
    if (!raw) {
      return { openChatCampaignIds: [], activeChatCampaignId: null };
    }
    const parsed = JSON.parse(raw);
    return {
      openChatCampaignIds: Array.isArray(parsed.openChatCampaignIds)
        ? parsed.openChatCampaignIds.map(String)
        : [],
      activeChatCampaignId: parsed.activeChatCampaignId
        ? String(parsed.activeChatCampaignId)
        : null,
    };
  } catch {
    return { openChatCampaignIds: [], activeChatCampaignId: null };
  }
};

export const saveOpenChatStateToStorage = (
  openChatCampaignIds,
  activeChatCampaignId,
) => {
  sessionStorage.setItem(
    OPEN_CHAT_STATE_KEY,
    JSON.stringify({
      openChatCampaignIds,
      activeChatCampaignId,
    }),
  );
};

export const clearOpenChatStateFromStorage = () => {
  sessionStorage.removeItem(OPEN_CHAT_STATE_KEY);
};
